import store from '~/Store';
import { preloadedState } from '~/api/auction';
import Account from '~/account/Account';
import AuctionProcessor from '~/processors/AuctionProcessor';
import { preloaded_state } from '~/api/mocks/auction';
import { LocalStorageHelper } from '~/utils/storage';
import { devModeKeyMap } from '~/utils/devMode';
import { isAuctionTabDisabled } from '~/utils/auction';
import { AUCTION_STATUS } from '~/const';
import Cron from '~/utils/Cron';
import { PreCacheImage } from '~/utils/preCacheImage';
import { auctionActions } from '~/Store/auctionSlide';

class AuctionRepository {
    public async preloadedState() {
        if (LocalStorageHelper.get(devModeKeyMap.mockAuction)) {
            return preloaded_state;
        }

        return await preloadedState(Account.getAccount()?.id);
    }
}

export default class AuctionStorage {
    static isAuctionCategoryEnabled = false;

    static isAlreadyPreloadedContent = false;

    static hasActiveAuction = false;

    static prepareAuctions(auctions: IAuction[]): IAuction[] {
        return auctions
            .map((auction: IAuction) => {
                const promotionStartedAt = new Date(auction.promotionStartedAt).getTime();
                const promotionFinishedAt = new Date(auction.promotionFinishedAt).getTime();
                const startedAt = new Date(auction.startedAt).getTime();
                const finishedAt = new Date(auction.finishedAt).getTime();
                const now = Date.now();

                if (promotionStartedAt) {
                    if (now < promotionFinishedAt && now < startedAt) {
                        auction.isNeedToShowPromoPage = true;
                    }
                }

                if (now >= finishedAt && auction.status === AUCTION_STATUS.ACTIVE) {
                    auction.status = AUCTION_STATUS.CHOOSING_WINNERS;
                }

                return auction;
            })
            .filter((auction) => {
                return auction.lots.length >= 1;
            });
    }

    static preloadContent(auctions: IAuction[]) {
        AuctionStorage.isAlreadyPreloadedContent = true;
        const auctionContentFields: Array<keyof IAuction> = ['promotionStageImageUrl', 'backgroundUrl'];
        const lotContentFields: Array<keyof ILot> = ['previewImageUrl', 'gridImageUrl', 'purchasePreviewImageUrl'];
        const resources: string[] = [];

        auctions.forEach((auction) => {
            auctionContentFields.forEach((field) => {
                if (auction[field]) {
                    resources.push(auction[field] as string);
                }
            });
            auction.lots.forEach((lot) => {
                lotContentFields.forEach((field) => {
                    if (lot[field]) {
                        resources.push(lot[field] as string);
                    }
                });
            });
        });

        if (resources.length) {
            new PreCacheImage().add(...resources);
        }
    }

    static async preloadedState() {
        if (isAuctionTabDisabled()) {
            AuctionStorage.isAuctionCategoryEnabled = false;
            return;
        }

        try {
            const instance = new AuctionRepository();
            const response = await instance.preloadedState();
            if (response) {
                AuctionStorage.isAuctionCategoryEnabled = response.activeAuctions?.length > 0;
                await AuctionProcessor.checkAccountResponse(response.account);
                store.dispatch(
                    auctionActions.updateAuctionState({ auctions: AuctionStorage.prepareAuctions(response.activeAuctions || []), account: response.account, minimalWonBids: response.minimalWonBids }),
                );
                AuctionProcessor.checkWonBids(response.account?.activeBids || []);
                AuctionStorage.hasActiveAuction = response.activeAuctions?.some((auction: IAuction) => auction.status === AUCTION_STATUS.ACTIVE) || false;
                if (!AuctionStorage.isAlreadyPreloadedContent) {
                    AuctionStorage.preloadContent(response.activeAuctions);
                }
            }
        } catch (e) {
            AuctionStorage.isAuctionCategoryEnabled = false;
        }
    }

    static createScheduledTask() {
        const auctions = store.getState().auction.activeAuctions;

        if (!auctions || !auctions.length) {
            return;
        }

        auctions.forEach((auction: IAuction) => {
            const nowTime = Math.floor(Date.now() / 1000);
            const date = new Date(auction.finishedAt).getTime();
            const activeTill = Math.floor(date / 1000);
            const diff = activeTill - nowTime;

            if (diff > 86400) {
                return;
            }

            const callback = () => store.dispatch(auctionActions.finishAuction(auction.id));

            Cron.newScheduledTask(`auction_${auction.id}`, callback, date);
        });
    }
}
